/**
 * Sales Orders List Page
 * Version: 1.0.0
 * Created: 2025-12-07
 *
 * Dashboard for viewing and managing sales orders
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    Chip,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Typography,
    IconButton,
    Tooltip,
    Checkbox,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
    Add as AddIcon,
    Visibility as ViewIcon,
    PictureAsPdf as PdfIcon,
    Cancel as CancelIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { salesOrdersAPI } from '../../../api/salesOrders';
import { SalesOrder, SalesOrderListFilters, SalesOrderStatus } from '../../../types/SalesOrder';
import { formatDateForDisplay } from '../../../utils/dateUtils';

const SalesOrderList: React.FC = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [orders, setOrders] = useState<SalesOrder[]>([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [filters, setFilters] = useState<SalesOrderListFilters>({
        page: 1,
        limit: 20,
    });

    // Status color mapping
    const getStatusColor = (status: SalesOrderStatus): 'default' | 'info' | 'warning' | 'success' | 'secondary' => {
        const muiColors: Record<string, 'default' | 'info' | 'warning' | 'success' | 'secondary'> = {
            [SalesOrderStatus.DRAFT]: 'default',
            [SalesOrderStatus.CONFIRMED]: 'info',
            [SalesOrderStatus.COMPLETED]: 'success',
            [SalesOrderStatus.CANCELLED]: 'secondary',
        };

        return muiColors[status] || 'default';
    };

    // Format status for display
    const formatStatus = (status: string): string => {
        return status.replace(/_/g, ' ').toUpperCase();
    };

    // Load Orders
    const loadOrders = async () => {
        setLoading(true);
        try {
            const response = await salesOrdersAPI.getOrders(filters);
            setOrders(response.orders);
            setTotal(response.total);
        } catch (error: any) {
            console.error('Failed to load sales orders:', error);
            enqueueSnackbar(error.response?.data?.detail || 'Failed to load sales orders', {
                variant: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    // Handle cancel order
    const handleCancelOrder = async (orderId: string, soNumber: string) => {
        if (!window.confirm(`Cancel Sales Order ${soNumber}? All reserved stock will be released.`)) {
            return;
        }

        try {
            await salesOrdersAPI.cancelOrder(orderId);
            enqueueSnackbar('Sales order cancelled successfully', { variant: 'success' });
            loadOrders(); // Reload list
        } catch (error: any) {
            enqueueSnackbar(error.response?.data?.detail || 'Failed to cancel order', { variant: 'error' });
        }
    };

    // PDF Download handlers
    const handleDownloadPDF = async (soId: number, soNumber: string) => {
        try {
            const blob = await salesOrdersAPI.downloadSinglePDF(soId);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            // Clean filename (remove slashes)
            const filename = `${soNumber.replace(/\//g, '-')}.pdf`;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            console.error('Failed to download PDF:', error);
            enqueueSnackbar('Failed to download PDF', { variant: 'error' });
        }
    };

    const handleBulkExportPDF = async () => {
        if (selectedIds.length === 0) return;

        try {
            const blob = await salesOrdersAPI.downloadBulkPDF(selectedIds);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Generate filename with timestamp
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
            const filename = `Sales-Orders-Bulk-${timestamp}.pdf`;

            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);

            enqueueSnackbar(`Exported ${selectedIds.length} orders to PDF`, { variant: 'success' });
            setSelectedIds([]); // Optional: clear selection after export
        } catch (error: any) {
            console.error('Failed to export PDF:', error);
            enqueueSnackbar('Failed to export PDF', { variant: 'error' });
        }
    };

    // Bulk selection handlers
    const handleSelectAll = () => {
        if (selectedIds.length === orders.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(orders.map(o => o.id));
        }
    };

    const handleSelectRow = (id: number) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    // Bulk operations
    const handleBulkCancel = async () => {
        if (selectedIds.length === 0) {
            enqueueSnackbar('Please select at least one order to cancel', { variant: 'warning' });
            return;
        }

        if (!window.confirm(`Cancel ${selectedIds.length} orders? Stock will be released.`)) {
            return;
        }

        try {
            const response = await salesOrdersAPI.bulkCancel(selectedIds);
            enqueueSnackbar(
                `Cancelled ${response.succeeded}/${response.total} orders`,
                { variant: response.failed > 0 ? 'warning' : 'success' }
            );
            setSelectedIds([]);
            loadOrders();
        } catch (error: any) {
            console.error('Bulk cancel error:', error);
            const errorMessage = error.response?.data?.detail || 'Bulk cancel failed';
            enqueueSnackbar(errorMessage, { variant: 'error' });
        }
    };

    const handleBulkDelete = async () => {
        if (!window.confirm(`Delete ${selectedIds.length} orders? This action cannot be undone.`)) {
            return;
        }

        try {
            const response = await salesOrdersAPI.bulkDelete(selectedIds);
            enqueueSnackbar(
                `Deleted ${response.succeeded}/${response.total} orders`,
                { variant: response.failed > 0 ? 'warning' : 'success' }
            );
            setSelectedIds([]);
            loadOrders();
        } catch (error: any) {
            enqueueSnackbar('Bulk delete failed', { variant: 'error' });
        }
    };

    const handleBulkStatusUpdate = async (event: any) => {
        const newStatus = event.target.value;
        if (!newStatus) return;

        try {
            const response = await salesOrdersAPI.bulkUpdateStatus(selectedIds, newStatus);
            enqueueSnackbar(
                `Updated ${response.succeeded}/${response.total} orders`,
                { variant: response.failed > 0 ? 'warning' : 'success' }
            );
            setSelectedIds([]);
            loadOrders();
        } catch (error: any) {
            enqueueSnackbar('Bulk status update failed', { variant: 'error' });
        }
    };

    // Inline editing handlers
    const handleQuickEdit = async (soId: number, field: string, value: any) => {
        try {
            // Optimistic update
            setOrders(prev => prev.map(order =>
                order.id === soId ? { ...order, [field]: value } : order
            ));

            // API call
            await salesOrdersAPI.updateOrder(soId.toString(), { [field]: value });

            enqueueSnackbar('Updated successfully', { variant: 'success' });
        } catch (error: any) {
            console.error('Quick edit failed:', error);
            enqueueSnackbar(error.response?.data?.detail || 'Update failed', { variant: 'error' });
            // Revert on error
            loadOrders();
        }
    };

    // DataGrid columns
    const columns: GridColDef[] = [
        {
            field: 'select',
            headerName: '',
            width: 50,
            sortable: false,
            disableColumnMenu: true,
            renderHeader: () => (
                <Checkbox
                    checked={orders.length > 0 && selectedIds.length === orders.length}
                    indeterminate={selectedIds.length > 0 && selectedIds.length < orders.length}
                    onChange={handleSelectAll}
                />
            ),
            renderCell: (params) => (
                <Checkbox
                    checked={selectedIds.includes(params.row.id)}
                    onChange={() => handleSelectRow(params.row.id)}
                />
            ),
        },
        {
            field: 'so_number',
            headerName: 'SO #',
            width: 140,
            renderCell: (params) => (
                <Typography
                    variant="body2"
                    sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 500 }}
                    onClick={() => navigate(`/outward/sales-orders/${params.row.id}`)}
                >
                    {params.value}
                </Typography>
            ),
        },
        { field: 'customer_name', headerName: 'Customer', width: 200 },
        { field: 'order_source', headerName: 'Source', width: 130 },
        {
            field: 'order_date',
            headerName: 'Order Date',
            width: 130,
            valueFormatter: (params) => formatDateForDisplay(params.value),
        },
        {
            field: 'delivery_date',
            headerName: 'Delivery Date',
            width: 150,
            editable: true,
            type: 'date',
            valueGetter: (params) => params.value ? new Date(params.value) : null,
            valueFormatter: (params) => params.value ? formatDateForDisplay(params.value) : '-',
            valueSetter: (params) => {
                const newDate = params.value ? new Date(params.value).toISOString().split('T')[0] : null;
                handleQuickEdit(params.row.id, 'delivery_date', newDate);
                return { ...params.row, delivery_date: newDate };
            },
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 180,
            editable: true,
            type: 'singleSelect',
            valueOptions: Object.values(SalesOrderStatus),
            renderCell: (params) => (
                <Chip
                    label={formatStatus(params.value)}
                    color={getStatusColor(params.value)}
                    size="small"
                    variant={params.value === SalesOrderStatus.DRAFT ? 'outlined' : 'filled'}
                />
            ),
            valueSetter: (params) => {
                handleQuickEdit(params.row.id, 'status', params.value);
                return { ...params.row, status: params.value };
            },
        },
        {
            field: 'total_amount',
            headerName: 'Amount (₹)',
            width: 130,
            valueFormatter: (params) => `₹${Number(params.value).toFixed(2)}`,
        },
        {
            field: 'notes',
            headerName: 'Notes',
            width: 200,
            editable: true,
            renderCell: (params) => (
                <Typography variant="body2" noWrap>
                    {params.value || '-'}
                </Typography>
            ),
            valueSetter: (params) => {
                handleQuickEdit(params.row.id, 'notes', params.value);
                return { ...params.row, notes: params.value };
            },
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="View/Edit">
                        <IconButton
                            size="small"
                            onClick={() => navigate(`/outward/sales-orders/${params.row.id}`)}
                        >
                            <ViewIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Download PDF">
                        <IconButton
                            size="small"
                            onClick={() => handleDownloadPDF(params.row.id, params.row.so_number)}
                        >
                            <PdfIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel Order">
                        <span>
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleCancelOrder(params.row.id, params.row.so_number)}
                                disabled={params.row.status === 'cancelled' || params.row.status === 'completed'}
                            >
                                <CancelIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5">Sales Orders</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/outward/sales-orders/new')}
                >
                    Create Sales Order
                </Button>
            </Box>

            {/* Bulk Action Toolbar */}
            {selectedIds.length > 0 && (
                <Card sx={{ p: 2, mb: 2, bgcolor: 'success.light', display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'success.dark' }}>
                        {selectedIds.length} selected
                    </Typography>

                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<PdfIcon />}
                        onClick={handleBulkExportPDF}
                        sx={{
                            bgcolor: 'white',
                            color: 'primary.main',
                            '&:hover': { bgcolor: 'grey.100' }
                        }}
                    >
                        Export PDF
                    </Button>

                    <Button
                        variant="contained"
                        color="warning"
                        size="small"
                        onClick={handleBulkCancel}
                        sx={{
                            bgcolor: 'white',
                            color: 'warning.dark',
                            '&:hover': { bgcolor: 'warning.light' }
                        }}
                    >
                        Cancel Selected
                    </Button>

                    <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={handleBulkDelete}
                        sx={{
                            bgcolor: 'white',
                            color: 'error.dark',
                            '&:hover': { bgcolor: 'error.light' }
                        }}
                    >
                        Delete Selected
                    </Button>

                    <FormControl size="small" sx={{ minWidth: 200, bgcolor: 'white', borderRadius: 1 }}>
                        <InputLabel>Update Status</InputLabel>
                        <Select
                            label="Update Status"
                            onChange={handleBulkStatusUpdate}
                            defaultValue=""
                        >
                            <MenuItem value="confirmed">Confirmed</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                        </Select>
                    </FormControl>

                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setSelectedIds([])}
                        sx={{
                            bgcolor: 'white',
                            borderColor: 'success.dark',
                            color: 'success.dark',
                            '&:hover': {
                                bgcolor: 'grey.100',
                                borderColor: 'success.dark'
                            }
                        }}
                    >
                        Clear Selection
                    </Button>
                </Card>
            )}

            {/* Filters */}
            <Card sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={filters.status || ''}
                            label="Status"
                            onChange={(e) => setFilters({ ...filters, status: e.target.value as SalesOrderStatus, page: 1 })}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value={SalesOrderStatus.DRAFT}>Draft</MenuItem>
                            <MenuItem value={SalesOrderStatus.CONFIRMED}>Confirmed</MenuItem>
                            <MenuItem value={SalesOrderStatus.COMPLETED}>Completed</MenuItem>
                            <MenuItem value={SalesOrderStatus.CANCELLED}>Cancelled</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        label="From Date"
                        type="date"
                        value={filters.from_date || ''}
                        onChange={(e) => setFilters({ ...filters, from_date: e.target.value, page: 1 })}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 180 }}
                    />

                    <TextField
                        label="To Date"
                        type="date"
                        value={filters.to_date || ''}
                        onChange={(e) => setFilters({ ...filters, to_date: e.target.value, page: 1 })}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 180 }}
                    />

                    {filters.status || filters.from_date || filters.to_date ? (
                        <Button
                            variant="outlined"
                            onClick={() => setFilters({ page: 1, limit: 20 })}
                        >
                            Clear Filters
                        </Button>
                    ) : null}
                </Box>
            </Card>

            {/* DataGrid */}
            <Card>
                <DataGrid
                    rows={orders}
                    columns={columns}
                    loading={loading}
                    pageSizeOptions={[20, 50, 100]}
                    paginationMode="server"
                    rowCount={total}
                    paginationModel={{ page: filters.page! - 1, pageSize: filters.limit! }}
                    onPaginationModelChange={(model) =>
                        setFilters({ ...filters, page: model.page + 1, limit: model.pageSize })
                    }
                    autoHeight
                    disableRowSelectionOnClick
                    sx={{
                        '& .MuiDataGrid-cell:focus': {
                            outline: 'none',
                        },
                    }}
                />
            </Card>
        </Box>
    );
};

export default SalesOrderList;
